import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import type { BulkDisposeConditionCode, ManualEntryItem } from '@/components/bulk-dispose/types';
import { configAdminService } from '@/services/configAdminService';

const CONDITION_CODE_OPTIONS: BulkDisposeConditionCode[] = ['H', 'A', 'B'];
const Z_EXPENDABLE_LIN = 'ZEXPEND';
const FALLBACK_DEFAULT_NSN = '8415-ZEXPEND';

type AddManualEntryDialogProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (item: ManualEntryItem) => void;
};

export const AddManualEntryDialog: React.FC<AddManualEntryDialogProps> = ({
  open,
  onClose,
  onAdd,
}) => {
  const [lin, setLin] = useState(Z_EXPENDABLE_LIN);
  const [nsn, setNsn] = useState('');
  const [nomenclature, setNomenclature] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [conditionCode, setConditionCode] = useState<BulkDisposeConditionCode>('H');
  const [remarks, setRemarks] = useState('');
  const [defaultNsn, setDefaultNsn] = useState(FALLBACK_DEFAULT_NSN);

  // Fetch the configured default NSN for Z expendable entries
  useEffect(() => {
    let cancelled = false;
    configAdminService.list('z_expendable_default_nsn').then((rows) => {
      if (cancelled) return;
      const value = rows?.[0]?.config_value;
      if (typeof value === 'string' && value.trim().length > 0) {
        setDefaultNsn(value.trim());
      }
    }).catch(() => { /* use fallback */ });
    return () => { cancelled = true; };
  }, []);

  // Auto-populate NSN when LIN matches Z expendable and NSN hasn't been manually edited
  const [nsnManuallyEdited, setNsnManuallyEdited] = useState(false);

  useEffect(() => {
    if (!nsnManuallyEdited && lin.trim().toUpperCase() === Z_EXPENDABLE_LIN) {
      setNsn(defaultNsn);
    }
  }, [lin, defaultNsn, nsnManuallyEdited]);

  const handleNsnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNsnManuallyEdited(true);
    setNsn(e.target.value);
  }, []);

  const resetForm = useCallback(() => {
    setLin(Z_EXPENDABLE_LIN);
    setNsn(defaultNsn);
    setNomenclature('');
    setQuantity('1');
    setConditionCode('H');
    setRemarks('');
    setNsnManuallyEdited(false);
  }, [defaultNsn]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const parsedQuantity = Math.floor(Number(quantity));
  const isQuantityValid = Number.isFinite(parsedQuantity) && parsedQuantity >= 1;
  const isValid = lin.trim().length > 0 && nomenclature.trim().length > 0 && isQuantityValid;

  const handleAdd = useCallback(() => {
    if (!isValid) return;

    const item: ManualEntryItem = {
      id: crypto.randomUUID(),
      lin: lin.trim(),
      nsn: nsn.trim(),
      nomenclature: nomenclature.trim(),
      quantity: parsedQuantity,
      conditionCode,
      remarks: remarks.trim(),
      createdAt: new Date().toISOString(),
    };

    onAdd(item);
    resetForm();
    onClose();
  }, [isValid, lin, nsn, nomenclature, parsedQuantity, conditionCode, remarks, onAdd, resetForm, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Manual Entry</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="LIN"
            value={lin}
            onChange={(e) => setLin(e.target.value)}
            required
            fullWidth
            helperText="Line Item Number (defaults to ZEXPEND)"
          />
          <TextField
            label="NSN"
            value={nsn}
            onChange={handleNsnChange}
            fullWidth
            placeholder="e.g. 8470-00-X15-8919"
            helperText={lin.trim().toUpperCase() === Z_EXPENDABLE_LIN ? 'Defaults to configured Z expendable NSN' : undefined}
          />
          <TextField
            label="Nomenclature"
            value={nomenclature}
            onChange={(e) => setNomenclature(e.target.value)}
            required
            fullWidth
            placeholder="Item description for the DD 1348"
          />
          <TextField
            label="Quantity"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            required
            fullWidth
            slotProps={{ htmlInput: { min: 1 } }}
            error={quantity !== '' && !isQuantityValid}
            helperText={quantity !== '' && !isQuantityValid ? 'Quantity must be at least 1' : undefined}
          />
          <TextField
            label="Condition Code"
            select
            value={conditionCode}
            onChange={(e) => setConditionCode(e.target.value as BulkDisposeConditionCode)}
            fullWidth
          >
            {CONDITION_CODE_OPTIONS.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder="Additional data for the DD 1348 (optional)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleAdd} variant="contained" disabled={!isValid}>
          Add Entry
        </Button>
      </DialogActions>
    </Dialog>
  );
};
